<template>
  <v-container class="py-8 bg-light-blue-main">
    <h1>{{ $t("views.organization.edit") }}</h1>

    <v-card>
      <v-card-text>
        <v-form>
          <v-text-field
            class="mb-4 mt-2"
            v-model="name"
            :rules="[required]"
            label="Name" />
          <v-text-field
            v-model="description"
            label="Description" />
        </v-form>

        <v-data-table
          :headers="userListHeaders"
          :items="userListItems">
          <template v-slot:header.actions="{}">
            <v-btn
              v-if="allowedEdit"
              @click="addUser()"
              ><v-icon>mdi-plus</v-icon></v-btn
            >
          </template>
          <template v-slot:item.licence="{ item }">
            <v-switch
              color="green"
              :false-value="0"
              :true-value="1"
              v-model="item.licence"
              :label="item.licence ? 'Active' : 'Inactive'"></v-switch>
          </template>
          <template
            v-if="allowedEdit"
            v-slot:item.actions="{ item, index }">
            <v-btn
              v-if="index > 0 && !item.pending"
              @click="editUser(item, index)"
              ><v-icon>mdi-pencil</v-icon></v-btn
            >
            <v-btn
              v-if="index > 0"
              @click="removeUser(index)"
              ><v-icon>mdi-delete</v-icon></v-btn
            >
          </template>
        </v-data-table>
        <v-dialog
          v-model="userDialog.dialog"
          persistent>
          <v-card>
            <v-card-title> Edit User </v-card-title>
            <v-card-text>
              <v-form ref="userForm">
                <v-text-field
                  class="mt-2"
                  v-model="userDialog.email"
                  :rules="emailRules"
                  label="Email" />
                <v-select
                  class="mt-4"
                  v-model="userDialog.licence"
                  :items="userLicence"
                  label="Licence" />
                <v-select
                  class="mt-4"
                  v-model="userDialog.rights"
                  :items="userRights"></v-select>
              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-btn @click="userDialog.callback()">Save</v-btn>
              <v-btn @click="userDialog.dialog = false">Cancel</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
    <v-card
      v-if="userLicences > 0"
      class="pb-5">
      <ModuleList
        :allowedEdit="!!allowedEdit"
        ref="moduleList"
        :showSaveButton="false"
        :modules="modulesBefore"
        v-model="modules"
        :monthEnd="modulesMonthEnd"
        :userCount="userLicences"></ModuleList>
    </v-card>
    <v-card v-if="allowedEdit">
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="saveChanges">Speichern</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, useTemplateRef } from "vue";
import ModuleList from "../Licence/ModuleList.vue";
import { useUserRules } from "@/validations/user";
import { useCommonRules } from "@/validations/common";
import { useTranslation } from "i18next-vue";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import axios from "axios";
import {
  ORGANIZATION_RIGHT_COOWNER,
  ORGANIZATION_RIGHT_OWNER,
  ORGANIZATION_RIGHT_READ,
  organizationRightNames
} from "@/utils/constants";
import RightsElement from "@/components/Elements/RightsElement.vue";
import { useAxiosStore } from "@/stores/axiosStore";
import { useRoute, useRouter } from "vue-router";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";

const createConfirm = useConfirm();
const createSnackbar = useSnackbar();
const route = useRoute();
const router = useRouter();

const allowedEdit = computed(() => {
  return (
    mainStore.organizationRights(route.params.id as string) &
    (ORGANIZATION_RIGHT_OWNER | ORGANIZATION_RIGHT_COOWNER)
  );
});

type User = {
  email: string;
  rights: number;
  licence: number;
  state: number;
  pending?: boolean;
};

const organizationId = computed(() => route.params.id);

const mainStore = useMainStore();
const authStore = useAuthStore();
const axiosStore = useAxiosStore();

const { t } = useTranslation();

const { emailRules } = useUserRules(t);
const { required } = useCommonRules(t);

const licenceId = ref("0");

const moduleListRef = useTemplateRef("moduleList");

onMounted(() => {
  loadOrganization();
});

async function loadOrganization() {
  const {
    data: { organization, licence }
  } = await axios.get("/api/organization/" + organizationId.value);

  name.value = organization.name;
  description.value = organization.description;

  modules.value = licence.modules;
  modulesBefore.value = licence.modules;
  modulesMonthEnd.value = licence.removedModules;

  console.log(licence, organization);

  for (const user of organization.organization_user) {
    userListItems.value.push({
      email: user.users.email,
      rights: user.rights,
      licence: user.licence ? 1 : 0,
      state: user.state
    });
  }

  for (const user of organization.pending_invites) {
    userListItems.value.push({
      email: user.email,
      rights: user.rights,
      licence: user.licence ? 1 : 0,
      state: 0,
      pending: true
    });
  }

  licenceId.value = licence.id;
}

const name = ref("");
const description = ref("");
const mode = ref(0);
const modules = ref(1);
const modulesBefore = ref(0);
const modulesMonthEnd = ref(0);
const userListItems: Ref<User[]> = ref([]);

const userLicences = computed(() => {
  return userListItems.value.filter((user) => user.licence === 1).length;
});

const userRights = Object.entries(organizationRightNames).map(
  ([key, value]) => ({
    title: t("other.rights." + value),
    value: Number(key)
  })
);
const userLicence = [
  {
    title: "yes",
    value: 1
  },
  {
    title: "no",
    value: 0
  }
];

const userListHeaders = [
  { title: "email", value: "email" },
  {
    title: "rights",
    key: "rights",
    value: (item) =>
      userRights
        .filter((option) => option.value & item.rights)
        .map((option) => option.title)
        .join(", ")
  },
  { title: "licence", value: "licence" },
  {
    title: "",
    key: "state",
    value: (item) => {
      console.log("state", item.state);
      return item.state ? "" : t("other.state.inactive");
    }
  },
  { title: "actions", value: "actions" }
];

const userFormRef = useTemplateRef("userForm");

const userDialog = ref({
  dialog: false,
  email: "",
  rights: ORGANIZATION_RIGHT_READ,
  licence: 0,
  callback: () => {}
});

const userListEmails = computed(() => {
  return userListItems.value.map((item) => item.email);
});

function addUser() {
  userDialog.value = {
    dialog: true,
    email: "",
    rights: ORGANIZATION_RIGHT_READ,
    licence: 0,
    callback: () => {
      userFormRef.value.validate().then((valid) => {
        if (valid.valid) {
          if (
            userDialog.value.email &&
            !userListEmails.value.includes(userDialog.value.email)
          ) {
            userListItems.value.push({
              email: userDialog.value.email,
              rights: userDialog.value.rights,
              licence: userDialog.value.licence
            });
            userDialog.value.dialog = false;
          }
        }
      });
    }
  };
}

function editUser(item: User, index: number) {
  userDialog.value = {
    dialog: true,
    email: item.email,
    rights: item.rights,
    licence: item.licence,
    callback: () => {
      userListItems.value.splice(index, 1, {
        email: userDialog.value.email,
        rights: userDialog.value.rights,
        licence: userDialog.value.licence
      });
      userDialog.value.dialog = false;
    }
  };
}

function removeUser(index: number) {
  userListItems.value.splice(index, 1);
}

function saveChanges() {
  console.log("saveChanges");

  if (!moduleListRef.value) {
    axiosStore.execute({
      method: "POST",
      url: "/api/organization/edit/" + organizationId.value,
      data: {
        name: name.value,
        description: description.value,
        modules: modules.value,
        slots: userLicences.value,
        users: userListItems.value,
        licenceId: licenceId.value
      }
    });
    return;
  }

  const { monthlyCost, additionalCharge, modulesRemoved } =
    moduleListRef.value.getInfos();

  // payment for remaining days of the month
  //
  createConfirm({
    dialogProps: { width: 400 },
    cardProps: { color: "warning" },
    title: t(`actions.confirmation.change-licence.title`),
    content:
      t(`actions.confirmation.change-licence.content`, {
        newPrice: monthlyCost
      }) +
      (additionalCharge
        ? t(
            `actions.confirmation.change-licence.module-added`,
            additionalCharge
          )
        : "") +
      (modulesRemoved.length
        ? t(`actions.confirmation.change-licence.module-removed`, {
            removedModules: modulesRemoved
              .map((module) => module.module)
              .join(", ")
          })
        : ""),
    cardTextProps: { style: { "white-space": "pre-wrap" } },
    confirmationText: t(`actions.confirmation.change-licence.confirm`),
    cancellationText: t(`actions.confirmation.change-licence.cancel`)
    // confirmationButtonProps: { color: "warning" }
  }).then((confirm) => {
    if (confirm) {
      axiosStore.execute(
        {
          method: "POST",
          url: "/api/organization/edit/" + organizationId.value,
          data: {
            name: name.value,
            description: description.value,
            modules: modules.value,
            slots: userLicences.value,
            users: userListItems.value,
            licenceId: licenceId.value
          }
        },
        () => {
          router.push({ name: "profile" });
        }
      );

      createSnackbar({
        text: t("actions.saved"),
        showCloseButton: false,
        snackbarProps: { color: "error", timeout: 1000 }
      });
    }
  });
}
</script>
